import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import { get, onValue, push, ref, remove, update } from "firebase/database";
import { useEffect, useState } from "react";
import { db } from "../Firebase";
import TableFilters, { useTableFilters } from "./TableFilters";

const OtherMaster = () => {
  const [mandirFullName, setMandirFullName] = useState("");
  const [mandirNickName, setMandirNickName] = useState("");
  const [mandirAddress, setMandirAddress] = useState("");
  const [remark, setRemark] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [editId, setEditId] = useState(null);
  const [data, setData] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  /* ================= FILTERS ================= */
  const columns = [
    { key: 'state', label: 'State' },
    { key: 'district', label: 'District' }, 
    { key: 'fullName', label: 'Full Name of Jain Mandir' },
    { key: 'nickName', label: 'Nick Name of Jain Mandir' },
    { key: 'address', label: 'Full Address' },
    { key: 'remark', label: 'Remark' }
  ];
  const filters = useTableFilters(data, columns);
  const pendingCount = data.filter(m => m.pendingApproval === true).length;

  /* ================= FETCH ================= */
  useEffect(() => {
    // Fetch mandirs
    const unsubscribeMandirs = onValue(ref(db, "masters/jainMandir"), snap => {
      const list = snap.exists()
        ? Object.entries(snap.val()).map(([id, v]) => ({
            id,
            ...v,
          }))
        : [];
      setData(list);
    });

    // Fetch states
    const unsubscribeStates = onValue(ref(db, "masters/states"), snap => {
      const stateList = snap.exists()
        ? Object.entries(snap.val()).map(([id, v]) => ({
            id,
            ...v,
          }))
        : [];
      setStates(stateList);
    });

    // Fetch cities
    const unsubscribeCities = onValue(ref(db, "masters/cities"), snap => {
      const cityList = snap.exists()
        ? Object.entries(snap.val()).map(([id, v]) => ({
            id,
            ...v,
          }))
        : [];
      setCities(cityList);
    });

    return () => {
      unsubscribeMandirs();
      unsubscribeStates();
      unsubscribeCities();
    };
  }, []);

  /* ================= RESET ================= */
  const resetForm = () => {
    setMandirFullName("");
    setMandirNickName("");
    setMandirAddress("");
    setRemark("");
    setSelectedState("");
    setSelectedDistrict("");
    setEditId(null);
  };

  /* ================= APPROVE ================= */
  const handleApprove = async id => {
    await update(ref(db, `masters/jainMandir/${id}`), { pendingApproval: false });
    alert('मंदिर स्वीकृत कर दिया गया ✅');
  };

  /* ================= CASCADE UPDATE REGISTRATIONS ================= */
  const cascadeNickNameUpdate = async (oldNickName, newNickName) => {
    if (oldNickName === newNickName) return;
    const snap = await get(ref(db, "registration"));
    if (!snap.exists()) return;
    const updates = {};
    Object.entries(snap.val()).forEach(([regId, reg]) => {
      if (reg.basic?.Mandirtype === oldNickName)
        updates[`registration/${regId}/basic/Mandirtype`] = newNickName;
    });
    if (Object.keys(updates).length > 0) await update(ref(db), updates);
  };

  /* ================= SAVE ================= */
  const handleSave = async () => {
    if (!mandirFullName.trim() || !mandirNickName.trim() || !mandirAddress.trim() || !selectedState.trim() || !selectedDistrict.trim()) {
      return alert("All fields are required");
    }

    const mandirData = {
      state: selectedState,
      district: selectedDistrict,
      fullName: mandirFullName,
      nickName: mandirNickName,
      address: mandirAddress,
      remark: remark || ""
    };

    try {
      if (editId) {
        const existing = data.find(d => d.id === editId);
        const { id: _id, ...existingFields } = existing || {};
        await update(ref(db, `masters/jainMandir/${editId}`), { ...existingFields, ...mandirData });
        await cascadeNickNameUpdate(existing.nickName, mandirNickName);
      } else {
        await push(ref(db, "masters/jainMandir"), mandirData);
      }
      resetForm();
    } catch (err) {
      console.error(err);
      alert("Save failed ❌: " + err.message);
    }
  };

  /* ================= EDIT ================= */
  const handleEdit = row => {
    setEditId(row.id);
    setMandirFullName(row.fullName || "");
    setMandirNickName(row.nickName || "");
    setMandirAddress(row.address || "");
    setRemark(row.remark || "");
    setSelectedState(row.state || "");
    setSelectedDistrict(row.district || "");
  };

  /* ================= DELETE ================= */
  const handleDelete = async id => {
    if (window.confirm("Are you sure you want to delete this mandir?")) {
      await remove(ref(db, `masters/jainMandir/${id}`));
    }
  };

  // Get available districts based on selected state
  const availableDistricts = cities.filter(city => city.state === selectedState);

  /* ================= UI ================= */
  return (
    <div className="container mt-4">
      <h4 className="mb-3">🛕 Jain Mandir Master</h4>

      {/* FORM */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">State</label>
              <select
                className="form-control"
                value={selectedState}
                onChange={e => setSelectedState(e.target.value)}
              >
                <option value="">Select State</option>
                {states.map(state => (
                  <option key={state.id} value={state.name}>
                    {state.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label">District</label>
              <select
                className="form-control"
                value={selectedDistrict}
                onChange={e => setSelectedDistrict(e.target.value)}
                disabled={!selectedState}
              >
                <option value="">Select District</option>
                {availableDistricts.map(district => (
                  <option key={district.id} value={district.name}>
                    {district.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label">Full Name of Jain Mandir</label>
              <input
                className="form-control"
                value={mandirFullName}
                onChange={e => setMandirFullName(e.target.value)}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Nick Name of Jain Mandir</label>
              <input
                className="form-control"
                value={mandirNickName}
                onChange={e => setMandirNickName(e.target.value)}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Full Address</label>
              <textarea
                className="form-control"
                value={mandirAddress}
                onChange={e => setMandirAddress(e.target.value)}
                rows="3"
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Remark</label>
              <textarea
                className="form-control"
                value={remark}
                onChange={e => setRemark(e.target.value)}
                rows="3"
              />
            </div>
          </div>

          <div className="mt-3">
            <button className="btn btn-primary me-2" onClick={handleSave}>
              {editId ? "Update" : "Save"}
            </button>
            {editId && (
              <button className="btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>

      {pendingCount > 0 && (
        <div className="alert alert-warning py-2 mb-3">
          ⚠️ <strong>{pendingCount} मंदिर</strong> उपयोगकर्ता द्वारा जोड़े गए हैं और स्वीकृति की प्रतीक्षा में हैं।
        </div>
      )}

      {/* TABLE */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>Jain Mandir List <span className="badge bg-info ms-2">Total: {filters.filteredData.length}/{data.length}</span>{pendingCount > 0 && <span className="badge bg-warning text-dark ms-2">{pendingCount} Pending</span>}</h5>
      </div>
      <TableFilters
        searchTerm={filters.searchTerm}
        setSearchTerm={filters.setSearchTerm}
        sortField={filters.sortField}
        setSortField={filters.setSortField}
        sortOrder={filters.sortOrder}
        setSortOrder={filters.setSortOrder}
        columns={columns}
        placeholder="Search mandirs..."
      />
      <table className="table table-bordered" style={{ fontSize: "0.8rem" }}>
        <thead className="table-dark">
          <tr>
            <th style={{ fontSize: "0.75rem" }}>#</th>
            <th style={{ fontSize: "0.75rem" }}>State</th>
            <th style={{ fontSize: "0.75rem" }}>District</th>
            <th style={{ fontSize: "0.75rem" }}>Full Name of Jain Mandir</th>
            <th style={{ fontSize: "0.75rem" }}>Nick Name of Jain Mandir</th>
            <th style={{ fontSize: "0.75rem" }}>Full Address</th>
            <th style={{ fontSize: "0.75rem" }}>Remark</th>
            <th style={{ fontSize: "0.75rem" }}>Status</th>
            <th style={{ width: "150px", fontSize: "0.75rem" }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {filters.filteredData.map((row, i) => (
            <tr key={row.id} style={row.pendingApproval === true ? { background: '#fff3cd' } : {}}>
              <td style={{ fontSize: "0.8rem" }}>{i + 1}</td>
              <td style={{ fontSize: "0.8rem" }}>{row.state || "-"}</td>
              <td style={{ fontSize: "0.8rem" }}>{row.district || "-"}</td>
              <td style={{ fontSize: "0.8rem" }}>{row.fullName || "-"}</td>
              <td style={{ fontSize: "0.8rem" }}>
                {row.nickName || "-"}
                {row.pendingApproval === true && <span className="badge bg-warning text-dark ms-1" style={{ fontSize: '0.65rem' }}>⏳</span>}
              </td>
              <td style={{ fontSize: "0.8rem" }}>{row.address || "-"}</td>
              <td style={{ fontSize: "0.8rem" }}>{row.remark || "-"}</td>
              <td style={{ fontSize: "0.8rem" }}>
                {row.pendingApproval === true
                  ? <span className="badge bg-warning text-dark">⏳ Pending</span>
                  : <span className="badge bg-success">✅ Approved</span>}
              </td>
              <td style={{ whiteSpace: "nowrap", fontSize: "0.8rem" }}>
                <div className="d-flex gap-1 flex-wrap">
                  {row.pendingApproval === true && (
                    <button
                      className="btn btn-xs btn-success px-2 py-1"
                      style={{ fontSize: "0.7rem" }}
                      onClick={() => handleApprove(row.id)}
                    >
                      ✅ Approve
                    </button>
                  )}
                  <button
                    className="btn btn-xs btn-warning px-2 py-1"
                    style={{ fontSize: "0.7rem" }}
                    onClick={() => handleEdit(row)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-xs btn-danger px-2 py-1"
                    style={{ fontSize: "0.7rem" }}
                    onClick={() => handleDelete(row.id)}
                  >
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          ))}

          {!filters.filteredData.length && (
            <tr>
              <td colSpan="8" className="text-center">
                No data available
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default OtherMaster;
